import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import ChecklistItem from '@repo/datalayer/store/models/ChecklistItem'
import { Image, Video } from 'lucide-react'
import { ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileUploadButton } from '../ChecklistPage/FileUploadButton'
import { observer } from 'mobx-react-lite'
import { set } from 'mobx'
import TemplateItem from '@repo/datalayer/store/models/TemplateItem'

export const EditTemplateItemDialog = observer(function EditItemDialog({
  item,
  children,
}: {
  children: ReactElement
  item: TemplateItem
}) {
  const [open, setOpen] = useState(false)
  const [fileUrl, setFileUrl] = useState<string | null>(
    item.descriptionFilename || null
  )
  const [requiresMedia, setRequiresMedia] = useState(item.requiresMedia)
  const { t } = useTranslation()
  const isVideo = item.descriptionFiletype?.match('video/')

  console.log('item', item.descriptionFilename)

  useEffect(() => {
    if (!item.descriptionFilename) return
    // alert('fo')
    setFileUrl(null)
    console.log('getting file url', item.descriptionFilename)
    item.getDescriptionFileUrl().then((data) => {
      console.log('got file url', data.url)
      setFileUrl(data.url)
    })
  }, [item.descriptionFilename])

  useEffect(() => {
    setRequiresMedia(item.requiresMedia)
  }, [item.requiresMedia])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>{t('checklists.editItem')}</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <form
          onSubmit={async (e) => {
            e.preventDefault()

            const form = e.target as HTMLFormElement
            const formData = new FormData(form)

            const payload: {
              name?: string
              description?: string
              requiresMedia?: boolean
            } = {}

            const name = formData.get('name') as string
            if (name !== item.name) {
              payload.name = name
            }
            const description = formData.get('description') as string
            if (description !== item.description) {
              payload.description = description
            }
            if (requiresMedia !== item.requiresMedia) {
              payload.requiresMedia = requiresMedia
            }

            if (Object.keys(payload).length > 0) {
              await item.update(payload)
            }

            setOpen(false)
          }}
        >
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {t('checklists.checklistName')}
              </Label>
              <Input
                id="name"
                name="name"
                data-test-id="edit-item-name"
                className="col-span-3"
                autoComplete="off"
                data-1p-ignore
                defaultValue={item.name}
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="name" className="text-right mt-2">
                {t('checklists.checklistDescription')}
              </Label>
              <Textarea
                id="description"
                name="description"
                data-test-id="edit-item-description"
                className="col-span-3 min-h-40"
                autoComplete="off"
                data-1p-ignore
                defaultValue={item.description}
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="requiresMedia" className="text-right">
                {t('checklists.requireMediaProof')}
              </Label>
              <div className="col-span-3 flex items-center gap-2">
                <Checkbox
                  id="requiresMedia"
                  data-test-id="edit-item-requires-media"
                  checked={requiresMedia}
                  onCheckedChange={(checked) =>
                    setRequiresMedia(Boolean(checked))
                  }
                />
                <span className="text-sm text-muted-foreground">
                  {t('checklists.requireMediaProofHelp')}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-4 grid-rows-2 items-start gap-4">
              <Label htmlFor="name" className="text-right mt-2">
                {t('checklists.checklistDescriptionMedia')}
              </Label>
              <div className="w-full h-full col-span-3 row-span-2">
                <div className="flex justify-end">
                  <FileUploadButton
                    item={item}
                    onUpload={(filename, filetype) => {
                      item.addDescriptionFile(filename, filetype)
                    }}
                  />
                </div>
                {fileUrl && !isVideo && <img key={fileUrl} src={fileUrl} />}
                {fileUrl && isVideo && (
                  <video
                    autoPlay
                    src={fileUrl}
                    controls
                    className="w-full h-auto"
                  />
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="!justify-between gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setOpen(false)}
            >
              {t('close')}
            </Button>
            <Button data-test-id="edit-item-save" variant="default" type="submit">
              {t('save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
})
